import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, MapPin, Video, ArrowRight, Loader2, Globe2 } from "lucide-react";
import { bookingsApi, BOOKING_TESTIDS as T } from "@/lib/bookingsApi";
import {
  dayKeyInTz,
  formatDayInTz,
  formatInTz,
  formatTimeInTz,
  isSameTz,
  localTz,
  shortModeLabel,
  tzOffsetLabel,
} from "@/lib/sessionFormat";

// ---------------------------------------------------------------------------
// BookingWidget
// ---------------------------------------------------------------------------
// Renders inside the CoachProfile sidebar. Two clarity rules drive the UI:
//   1. Every time is shown in the CLIENT's local timezone by default, and
//      we also spell out the coach's timezone so cross-tz bookings are
//      never ambiguous.
//   2. The session type ("Online" vs "In person") is a single-choice
//      button pair with icons + never both selected — no radios, no toggles.
// A live summary card appears the moment the client has picked mode + slot
// so they always see coach / mode / date / time / duration / price before
// they hit checkout.
// ---------------------------------------------------------------------------

const CLIENT_TZ = localTz();

function groupSlotsByDay(slots, viewTz) {
  const map = {};
  slots.forEach((s) => {
    const day = dayKeyInTz(s.session_at, viewTz);
    (map[day] ||= []).push(s);
  });
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
}

export default function BookingWidget({ coach }) {
  const [slotsData, setSlotsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(null);
  const [selected, setSelected] = useState(null);
  const initialMode = coach.online_available ? "online" : coach.in_person_available ? "in_person" : "online";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("coachly_client_email") || ""; } catch { return ""; }
  });
  const [name, setName] = useState(() => {
    try { return localStorage.getItem("coachly_client_name") || ""; } catch { return ""; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await bookingsApi.slots(coach.slug);
        setSlotsData(r);
      } finally { setLoading(false); }
    })();
  }, [coach.slug]);

  // If we arrived with ?rebook=1 auto-scroll into the widget so the CTA
  // from BookingSuccess feels one-tap.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("rebook") === "1") {
      const el = document.getElementById("booking-widget");
      const scroll = () => {
        if (window.__lenis?.scrollTo) window.__lenis.scrollTo(el, { offset: -80 });
        else el?.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      // Wait one frame so the widget has painted.
      setTimeout(scroll, 200);
    }
  }, []);

  const coachTz = slotsData?.timezone || coach.timezone || "UTC";
  const duration = slotsData?.session_duration_minutes || coach.session_duration_minutes || 60;
  const availableSlots = useMemo(
    () => (slotsData?.slots || []).filter((s) => s.available),
    [slotsData]
  );
  const byDay = useMemo(() => groupSlotsByDay(availableSlots, CLIENT_TZ), [availableSlots]);
  useEffect(() => { if (byDay.length && !day) setDay(byDay[0][0]); }, [byDay, day]);
  const daySlots = useMemo(() => byDay.find(([d]) => d === day)?.[1] || [], [byDay, day]);

  const disabledReason = !coach.is_approved
    ? "Coach not yet approved."
    : !coach.stripe_connected_account_id
    ? "Coach hasn't connected Stripe yet."
    : !coach.payments_ready
    ? "Coach onboarding is still in progress."
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selected) return setError("Pick a time first.");
    if (mode === "online" && !coach.online_available) return setError("This coach isn't taking online sessions right now.");
    if (mode === "in_person" && !coach.in_person_available) return setError("This coach isn't taking in-person sessions right now.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError("Enter a valid email.");
    if (!name.trim()) return setError("Enter your name.");
    setBusy(true);
    try {
      // Persist client identity for rebook + saves on the next visit.
      try {
        localStorage.setItem("coachly_client_email", email.trim().toLowerCase());
        localStorage.setItem("coachly_client_name", name.trim());
      } catch {}
      const r = await bookingsApi.checkout({
        coach_slug: coach.slug,
        client_email: email.trim(),
        client_name: name.trim(),
        session_at: selected,
        mode,
      });
      window.location.href = r.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed.";
      setError(msg); toast.error(msg); setBusy(false);
    }
  };

  const price = coach.price_per_session || coach.hourly_rate;
  const feePct = 10;
  const sameTz = isSameTz(coachTz, CLIENT_TZ);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      {/* Price + duration header */}
      <div className="eyebrow">§ Book a session</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span data-testid={T.priceValue || "booking-widget-price"} className="font-display font-medium text-4xl tracking-[-0.03em] text-slate-900">
          ${price}
        </span>
        <span className="text-sm text-slate-500">/ {duration} min</span>
      </div>
      <p className="mt-1 text-[12px] text-slate-400">Coachly fee: {feePct}% · included</p>

      {/* Session modes at a glance */}
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="booking-widget-modes">
        {coach.online_available && (
          <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-[#2563EB]/8 text-[#2563EB] text-[11.5px] font-semibold">
            <Video className="w-3 h-3" strokeWidth={2.4} /> Online available
          </span>
        )}
        {coach.in_person_available && (
          <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-[#10B981]/10 text-[#059669] text-[11.5px] font-semibold">
            <MapPin className="w-3 h-3" strokeWidth={2.4} /> In-person · {coach.city}
          </span>
        )}
      </div>

      {disabledReason ? (
        <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-[13px] text-amber-800">
          <div className="font-semibold">Not bookable yet</div>
          <p className="mt-1 text-amber-700">{disabledReason}</p>
        </div>
      ) : loading ? (
        <div className="mt-6 h-24 rounded-xl bg-slate-100 animate-pulse" />
      ) : byDay.length === 0 ? (
        <div className="mt-6 text-[13px] text-slate-500">
          No slots available in the next 14 days. Try messaging {coach.name.split(" ")[0]} directly.
        </div>
      ) : (
        <form onSubmit={submit} data-testid={T.slotPicker} className="mt-6 space-y-5">
          {/* Timezone banner — always spell out both sides so nobody guesses */}
          <div
            data-testid="booking-widget-tz-banner"
            className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12px] text-slate-600 inline-flex items-start gap-2"
          >
            <Globe2 className="w-3.5 h-3.5 mt-0.5 text-slate-500" />
            {sameTz ? (
              <span>
                Times shown in <span className="font-semibold text-slate-900">{CLIENT_TZ}</span>
                {tzOffsetLabel(CLIENT_TZ) && <> · {tzOffsetLabel(CLIENT_TZ)}</>} — same as coach.
              </span>
            ) : (
              <span>
                Times shown in <span className="font-semibold text-slate-900">your local time ({CLIENT_TZ})</span>.
                Coach is in <span className="font-semibold text-slate-900">{coachTz}</span>{tzOffsetLabel(coachTz) && <> ({tzOffsetLabel(coachTz)})</>}.
              </span>
            )}
          </div>

          {/* Session type toggle */}
          <div>
            <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Session type</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("online")}
                disabled={!coach.online_available}
                data-testid={T.modeOnline}
                aria-pressed={mode === "online"}
                className={`h-11 rounded-xl text-[13px] font-semibold border inline-flex items-center justify-center gap-2 transition-colors ${
                  mode === "online" && coach.online_available
                    ? "bg-[#2563EB] text-white border-[#2563EB]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
                }`}
              >
                <Video className="w-4 h-4" strokeWidth={2.2} /> Online
              </button>
              <button
                type="button"
                onClick={() => setMode("in_person")}
                disabled={!coach.in_person_available}
                data-testid={T.modeInPerson}
                aria-pressed={mode === "in_person"}
                className={`h-11 rounded-xl text-[13px] font-semibold border inline-flex items-center justify-center gap-2 transition-colors ${
                  mode === "in_person" && coach.in_person_available
                    ? "bg-[#10B981] text-white border-[#10B981]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
                }`}
              >
                <MapPin className="w-4 h-4" strokeWidth={2.2} /> In person
              </button>
            </div>
          </div>

          {/* Day pills — labels rendered in the client's tz */}
          <div>
            <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Pick a day</label>
            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1">
              {byDay.map(([d, list]) => {
                const iso = list[0].session_at;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setDay(d); setSelected(null); }}
                    data-testid={`booking-widget-day-${d}`}
                    className={`shrink-0 h-9 px-3 rounded-full text-[12px] font-medium border transition-colors ${
                      day === d ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {formatDayInTz(iso, CLIENT_TZ)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time grid */}
          <div>
            <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Pick a time</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {daySlots.map((s) => (
                <button
                  key={s.session_at}
                  type="button"
                  onClick={() => setSelected(s.session_at)}
                  data-testid={T.slotBtn(s.session_at)}
                  className={`h-10 rounded-lg text-[12.5px] font-medium border transition-colors ${
                    selected === s.session_at
                      ? "bg-[#2563EB] text-white border-[#2563EB]"
                      : "bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {formatTimeInTz(s.session_at, CLIENT_TZ)}
                </button>
              ))}
            </div>
          </div>

          {/* Live summary — appears once the client has enough info to see
              the full picture. */}
          {selected && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="booking-widget-summary"
              className="rounded-xl border border-slate-200 bg-white p-4 space-y-2"
            >
              <div className="eyebrow">§ You're about to book</div>
              <SummaryRow label="Coach" value={coach.name} testId="booking-summary-coach" />
              <SummaryRow
                label="Session type"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    {mode === "online" ? <Video className="w-3.5 h-3.5 text-[#2563EB]" /> : <MapPin className="w-3.5 h-3.5 text-[#10B981]" />}
                    {shortModeLabel(mode)}{mode === "in_person" && <> · {coach.city}</>}
                  </span>
                }
                testId="booking-summary-mode"
              />
              <SummaryRow
                label="Date & time"
                value={<span data-testid="booking-summary-datetime">{formatInTz(selected, CLIENT_TZ, { weekday: "long" })}</span>}
              />
              <SummaryRow
                label="Timezone"
                value={
                  <span data-testid="booking-summary-tz">
                    {CLIENT_TZ}{tzOffsetLabel(CLIENT_TZ) && <> ({tzOffsetLabel(CLIENT_TZ)})</>}
                    {!sameTz && (
                      <span className="block text-[11.5px] text-slate-500 mt-0.5">
                        Coach's local time: {formatInTz(selected, coachTz)} ({coachTz})
                      </span>
                    )}
                  </span>
                }
              />
              <SummaryRow label="Duration" value={`${duration} minutes`} />
              <SummaryRow label="Price" value={<span className="font-semibold text-slate-900">${price}</span>} />
            </motion.div>
          )}

          {/* Client details */}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid={T.clientName}
            placeholder="Your name"
            className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
            required
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={T.clientEmail}
            placeholder="you@domain.com"
            className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
            required
          />

          <button
            type="submit"
            disabled={busy || !selected}
            data-testid={T.submit}
            className="w-full h-12 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
            {busy ? "Redirecting…" : selected ? `Continue to payment · $${price}` : "Pick a time"}
            {selected && !busy && <ArrowRight className="w-4 h-4" />}
          </button>
          {error && <div data-testid={T.error} className="text-[13px] text-red-500">{error}</div>}
          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            Payment via Stripe Checkout. Coach receives payout after Coachly's {feePct}% fee.<br />
            By continuing you agree to our{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="text-slate-700 hover:text-[#2563EB] underline underline-offset-2">Terms</a>,{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-slate-700 hover:text-[#2563EB] underline underline-offset-2">Privacy Policy</a>, and{" "}
            <a href="/refund-policy" target="_blank" rel="noreferrer" className="text-slate-700 hover:text-[#2563EB] underline underline-offset-2">Refund &amp; Cancellation Policy</a>.
          </p>
        </form>
      )}
    </div>
  );
}

function SummaryRow({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 text-[13px]">
      <span className="text-slate-500">{label}</span>
      <span data-testid={testId} className="text-right text-slate-800 max-w-[60%]">{value}</span>
    </div>
  );
}
